/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/pages/auth/AuthPage`, `src/pages/home/HomePage`).
 */

import React from "react";
import {
  Redirect,
  Route,
  Switch,
  withRouter,
  useLocation,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { useLastLocation } from "react-router-last-location";
import HomePage from "../pages/home/HomePage";
import ErrorsPage from "../pages/errors/ErrorsPage";
import { LayoutContextProvider } from "../../_metronic";
import Layout from "../../_metronic/layout/Layout";
import * as routerHelpers from "../router/RouterHelpers";
import AuthPage from "../pages/auth/AuthPage";
import SsoAuth from "../pages/auth/SsoAuth";
import ForgotPassword from "../pages/password/ForgotPassword";
import ResetPassword from "../pages/password/ResetPassword";
import TermosUso from "../components/TermosUso";
import PoliticaDePrivacidade from "../pages/politica-privacidade/PoliticaPrivacidade";
import CompletarDados from "../pages/subconta/CompletarDados";
import TelaFilaAgendamentoIndex from "../pages/fila-exibicao-agendamentos/TelaFilaAgendamentosIndex";
import PreResetPassword from "../pages/password/PreResetPassword";
import VerifyEmail from "../pages/auth/VerifyEmail";

export const Routes = withRouter(({ history }) => {
  const lastLocation = useLastLocation();
  const location = useLocation();

  routerHelpers.saveLastLocation(lastLocation);
  const { isAuthorized, menuConfig, userLastLocation } = useSelector(
    ({ auth, urls, builder: { menuConfig }, flags }) => ({
      menuConfig,
      isAuthorized: auth.me != null,
      userLastLocation: routerHelpers.getLastLocation(),
    })
  );

  const exibirCompletarConta = useSelector(
    (state) => state.flags.exibir_completar_conta
  );
  const redirecionarCompletarConta = useSelector(
    (state) => state.flags.redirecionar_completar_conta
  );

  const acessoDom = JSON.parse(window.localStorage.getItem("acesso_dom"));

  const completouMigracao = JSON.parse(
    window.localStorage.getItem("completou_migracao")
  );
  const pendenteDocumentos = JSON.parse(
    window.localStorage.getItem("pendente_documentos")
  );

  return (
    /* Create `LayoutContext` from current `history` and `menuConfig`. */
    <LayoutContextProvider history={history} menuConfig={menuConfig}>
      <Switch>
        <Route path="/error" component={ErrorsPage} />
        <Route path="/auth/sso" component={SsoAuth} />
        <Route path="/pre-reset" component={PreResetPassword} />

        {exibirCompletarConta ? (
          <Route path="/completar-conta" component={CompletarDados} />
        ) : null}

        {redirecionarCompletarConta && isAuthorized ? (
          <Redirect to="/completar-conta" />
        ) : null}

        <Route
          path="/fila-agendamentos/:id"
          component={TelaFilaAgendamentoIndex}
        />

        {!isAuthorized && useLocation().pathname === "/password/forgot" ? (
          <Route path="/password/forgot" component={ForgotPassword} />
        ) : null}

        {isAuthorized && location.pathname === "/password/reset" && (
          <Redirect to={`/pre-reset${location.search}`} />
        )}

        {!isAuthorized && useLocation().pathname === "/password/reset" ? (
          <Route path="/password/reset" component={ResetPassword} />
        ) : null}

        <Route path="/termos-de-uso" component={TermosUso} />
        <Route
          path="/politica-de-privacidade"
          component={PoliticaDePrivacidade}
        />
        <Route path="/verify-email" component={VerifyEmail} />

        {!isAuthorized ? (
          /* Render auth page when user at `/auth` and not authorized. */
          <AuthPage />
        ) : (
          /* Otherwise redirect to root page (`/`) */
          <Redirect from="/auth" to={userLastLocation} />
        )}

        {!isAuthorized ? (
          /* Redirect to `/auth` when user is not authorized */
          <Redirect to="/auth/login" />
        ) : (
          <Layout>
            <HomePage userLastLocation={userLastLocation} />
          </Layout>
        )}
      </Switch>
    </LayoutContextProvider>
  );
});
